import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import type { PlatformUsageRecentEvent } from '../types';
import { formatTenantLabel, formatUserLabel } from '../utils/displayLabels';
import { isKnownPlatformUsageEvent, platformUsageEventLabelKey } from '../utils/eventLabels';

interface RecentActivityFeedProps {
  events: PlatformUsageRecentEvent[];
  locale: string;
  isLoading: boolean;
  isError: boolean;
}

function formatPagePath(path: string | null): string {
  if (!path || path === '/') return 'الرئيسية';

  return path.replace(/^\/+/, '').replace(/\/+$/, '');
}

function formatProduct(slug: string): string {
  return slug === 'warid' ? 'Wared POS' : slug;
}

export function RecentActivityFeed({
  events,
  locale,
  isLoading,
  isError,
}: RecentActivityFeedProps) {
  const { t } = useTranslation();
  const unknown = t('platformUsage.common.unknown');

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle className="text-base">{t('platformUsage.feed.title')}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : isError ? (
          <p className="px-6 py-10 text-center text-sm text-destructive">
            {t('platformUsage.feed.loadError')}
          </p>
        ) : events.length === 0 ? (
          <p className="px-6 py-10 text-center text-sm text-muted-foreground">
            {t('platformUsage.feed.emptyMessage')}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {events.map((event) => {
              const userLabel =
                event.userName ?? formatUserLabel(event.userExternalId, event.userId, unknown);
              const tenantLabel =
                event.tenantName ??
                formatTenantLabel(event.tenantExternalId, event.tenantId, unknown);
              const eventLabel = isKnownPlatformUsageEvent(event.eventType)
                ? t(platformUsageEventLabelKey(event.eventType))
                : t('platformUsage.events.unknown', { type: event.eventType });

              return (
                <li key={event.id} className="px-6 py-4">
                  <div className="space-y-2 text-right">
                    <div className="flex items-center justify-between gap-4">
                      <time
                        dateTime={event.occurredAt}
                        className="shrink-0 whitespace-nowrap text-xs tabular-nums text-muted-foreground"
                      >
                        {new Date(event.occurredAt).toLocaleString(locale)}
                      </time>

                      <div className="flex min-w-0 items-center gap-2">
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                          {eventLabel === 'نشط' ? 'نشاط' : eventLabel}
                        </span>

                        <span className="truncate font-mono text-xs text-muted-foreground">
                          {formatPagePath(event.pagePath)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-foreground">{userLabel}</p>
                      <p className="text-xs text-muted-foreground">
                        {event.userEmail ?? unknown}
                        {' · '}
                        {event.userPhone ?? unknown}
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-2 text-xs">
                      <span className="text-muted-foreground">🏢 {tenantLabel}</span>

                      <span className="rounded bg-muted px-2 py-1 text-foreground">
                        {formatProduct(event.productSlug)}
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
